import { useCallback, useEffect, useState } from 'react';

import {
  fetchPlaytestSessions,
  type SessionListFilters,
  type SessionListRow,
} from '../../games/playtest-sessions-data.js';
import { useShell } from '../../shell/use-shell.js';
import { usePlaytestEventLogTick } from './playtest-event-log-context.js';

// Data hook for the session-list pane. Owns:
//   * fetch on mount + on filter change.
//   * refetch when shell-context bumps playtestSessionsVersion (writes
//     to playtest_sessions by ingest / submit handlers).
//   * refetch when the section-scoped playtest_event_log subscription
//     ticks (see PlaytestEventLogProvider for why this is lifted out).

export function useSessionList(
  playtestId: string | null,
  filters: SessionListFilters,
): {
  rows: SessionListRow[];
  loading: boolean;
  reload: () => Promise<void>;
} {
  const { playtestSessionsVersion } = useShell();
  const eventLogTick = usePlaytestEventLogTick();
  const [rows, setRows] = useState<SessionListRow[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async (): Promise<void> => {
    if (playtestId === null) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const next = await fetchPlaytestSessions(playtestId, filters);
    setRows(next);
    setLoading(false);
  }, [
    playtestId,
    filters.state,
    filters.tester,
    filters.startedAfter,
  ]);

  useEffect(() => {
    void reload();
  }, [reload, playtestSessionsVersion, eventLogTick]);

  return { rows, loading, reload };
}
